import { useDrawer } from '#app/components/Drawer/hooks/useDrawer.js';
import { NavDrawer } from '#app/components/Drawer/NavDrawer/NavDrawer.js';
import { SlideUpDrawer } from '#app/components/Drawer/SlideUpDrawer/SlideUpDrawer.js';
import { animated } from '#app/components/Primitives/Animated.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { SHOW_AND_HIDE_ANIMATION_VARIANTS } from '#app/utilities/animation.js';
import { useMenuContext } from './context.js';
import { type MenuPanelProps } from './types.js';
import { useClickAway } from '@contra/react-hooks/useClickAway';
import { AnimatePresence } from 'framer-motion';
import { useRef } from 'react';
import { css, styled, useTheme } from 'styled-components';

const MenuPanelContainer = styled(animated.div)<{
  allowPanelOverflow?: boolean;
  darkMode: boolean;
  hasBorder?: boolean;
  hasShadow?: boolean;
  margin?: string;
  maxHeight?: number;
  maxWidth?: number;
}>`
  ${({
    allowPanelOverflow = false,
    darkMode,
    hasBorder = false,
    hasShadow = true,
    margin,
    maxHeight = undefined,
    maxWidth = undefined,
    theme,
  }) => css`
    margin: ${margin ? margin : '8px 0'};
    background: ${darkMode ? '#262626' : theme.colorsV2.white};
    overflow: ${allowPanelOverflow ? 'visible' : 'hidden'};

    ${maxWidth &&
    css`
      max-width: ${maxWidth}px;
    `}

    ${maxHeight &&
    css`
      max-height: ${maxHeight}px;
      overflow-y: auto;
    `}

    border-radius: 10px;

    box-shadow: ${hasShadow ? theme.boxShadowV2.basic20 : 'unset'};

    ${hasBorder &&
    css`
      border: 1px solid
        ${darkMode ? theme.colorsV2.gray90 : theme.colorsV2.white};
    `}
    & > :first-child {
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
    }

    & > :last-child {
      border-bottom-left-radius: 10px;
      border-bottom-right-radius: 10px;
    }
  `}
`;

// TODO: Remove `color` casting once types are updated
export const MenuPanel = ({
  allowPanelOverflow = false,
  children,
  closeButtonLabel = 'Close',
  closeMenuOnMouseOut = false,
  disableCloseOnOutsideClick = false,
  forceDesktopMenuLayout = false,
  forceMenuLayout = false,
  isMobileNavMenu = false,
  margin,
  maxHeight = undefined,
  maxWidth = undefined,
  removePaddingOnMobile = false,
  removeSlideUpCloseButton = false,
  slideUpButtonVariant = 'V2TertiaryOutline',
  topCloseButton = false,
  ...props
}: MenuPanelProps) => {
  const {
    darkMode,
    hasBorder,
    isOpen,
    popperAttributes,
    popperElement,
    popperStyles,
    referenceElement,
    toggleMenu,
  } = useMenuContext();
  const theme = useTheme();
  const isMobile = useIsMobile();

  // this should be refactored away to useOverlay from react aria
  useClickAway(popperElement, (event) => {
    if (
      isOpen &&
      referenceElement.current &&
      event.target instanceof Element &&
      !referenceElement.current.contains(event.target) &&
      !disableCloseOnOutsideClick
    ) {
      toggleMenu();
    }
  });

  const drawerRef = useRef<HTMLDivElement>(null);

  const {
    drawerProps,
    isOpen: drawerOpen,
    toggle: toggleDrawer,
  } = useDrawer({
    drawerRef,
    isOpen,
    onToggle: toggleMenu,
  });

  return !forceDesktopMenuLayout && (isMobile || forceMenuLayout) ? (
    isMobileNavMenu ? (
      <NavDrawer
        isOpen={drawerOpen}
        onClose={toggleDrawer}
        {...drawerProps}
      >
        {children}
      </NavDrawer>
    ) : (
      <SlideUpDrawer
        aria-label="Menu Drawer"
        buttonVariant={slideUpButtonVariant}
        darkMode={darkMode}
        isOpen={drawerOpen}
        onClose={toggleDrawer}
        ref={drawerRef}
        removeCloseButton={removeSlideUpCloseButton}
        removePaddingOnMobile={removePaddingOnMobile}
        topCloseButton={topCloseButton}
        {...drawerProps}
        closeButtonLabel={closeButtonLabel}
      >
        {children}
      </SlideUpDrawer>
    )
  ) : (
    <div
      ref={popperElement}
      style={{
        ...popperStyles['popper'],
        maxWidth: maxWidth ? maxWidth : 'unset',
        width: maxWidth ? '100%' : 'unset',
        zIndex: theme.zIndices.menu,
      }}
      {...popperAttributes['popper']}
      onMouseLeave={closeMenuOnMouseOut ? () => toggleDrawer() : undefined}
    >
      <AnimatePresence mode="wait">
        {isOpen &&
          popperAttributes?.['popper'] &&
          !popperAttributes?.['popper']['data-popper-reference-hidden'] && (
            <MenuPanelContainer
              allowPanelOverflow={allowPanelOverflow}
              animate="show"
              aria-label="Menu Items"
              darkMode={darkMode}
              exit="hide"
              hasBorder={hasBorder}
              initial="hide"
              key="menu"
              margin={margin}
              maxHeight={maxHeight}
              maxWidth={maxWidth}
              role="menu"
              tabIndex={0}
              variants={SHOW_AND_HIDE_ANIMATION_VARIANTS}
              {...props}
            >
              {children}
            </MenuPanelContainer>
          )}
      </AnimatePresence>
    </div>
  );
};
